/** @jsxRuntime classic */
/** @jsx jsx */
import * as React from 'react';
import {FC, useEffect, useState} from 'react';
import {Row, Text} from '@flexe/ui-components';
import * as moment from 'moment';
import {css, jsx} from '@emotion/react';
import tokens from '@flexe/ui-tokens';
import HeaderDetail from '../../shared/HeaderDetail';
import {Reservation, WarehouseData} from '../../shared/CommonInterfaces';
import {Inbound} from '../shared/InboundInterfaces';
import ShipperInboundContext from '../contexts/ShipperInboundContext';

export interface WarehouseInformationProps {
  container: Inbound;
  reservation: Reservation;
}

const WarehouseInformation: FC<WarehouseInformationProps> = (props: WarehouseInformationProps) => {
  const {userService, warehouseService} = React.useContext(ShipperInboundContext);
  const {
    reservation,
    container: {receivingWindow}
  } = props;
  const [warehouseData, setWarehouseData] = useState<WarehouseData>();
  const [userName, setUserName] = useState('');
  const [phone, setPhone] = useState('');
  const [receivingHrs, setReceivingHrs] = useState('');

  const IS_SHIPPER = true;

  useEffect(() => {
    async function loadWarehouseInfo(resId: number) {
      return await warehouseService.getWarehouseInfo(resId, IS_SHIPPER);
    }

    async function loadUserData(id: number, resId: number) {
      return await userService.getUserContactInfo(id, resId, IS_SHIPPER);
    }

    loadWarehouseInfo(reservation.id).then((response) => {
      if (response && response.errors.length === 0) {
        setWarehouseData(response.data.warehouses[0]);
        setPhone(response.data.warehouses[0].phone);
        if (
          receivingWindow?.startAt &&
          receivingWindow.startAt?.length !== 0 &&
          receivingWindow?.endAt &&
          receivingWindow.endAt?.length !== 0
        ) {
          setReceivingHrs(receivingWindow.startAt + ' to ' + receivingWindow.endAt);
        } else if (
          response.data.warehouses[0].receivingStartAt &&
          response.data.warehouses[0].receivingStartAt?.length !== 0 &&
          response.data.warehouses[0].receivingEndAt &&
          response.data.warehouses[0].receivingEndAt?.length !== 0
        ) {
          setReceivingHrs(
            moment(new Date(response.data.warehouses[0].receivingStartAt))
              .utc()
              .format('hh:mm A') +
              ' to ' +
              moment(new Date(response.data.warehouses[0].receivingEndAt))
                .utc()
                .format('hh:mm A')
          );
        } else {
          setReceivingHrs('Not Specified');
        }
        loadUserData(response.data.warehouses[0].contactUser?.id, reservation.id).then((userResponse) => {
          if (userResponse && userResponse.errors.length === 0) {
            setUserName(userResponse.data.firstName + ' ' + userResponse.data.lastName);
          }
        });
      }
    });
  }, []);

  return (
    <div
      id="inbound-warehouse-info"
      className="inbound-warehouse-info"
      css={css`
        padding: ${tokens.spacing.v300.value}px;
      `}
    >
      <div
        className="container-fluid"
        css={css`
          height: 100%;
        `}
      >
        <div className="contents-container">
          <header>
            <div className="title-aligned">
              <Text level="Body1" bold={true}>
                Warehouse Information
              </Text>
            </div>
          </header>
          <div>
            <Row childWidths={['fill', 'fill']} alignmentVertical={'top'} type={'fill'}>
              <div id="address-info">
                {HeaderDetail({label: 'Address:', value: ' '})}
                <Text level="Body2" tag="p" bold={false}>
                  {warehouseData?.name}
                </Text>
                <Text level="Body2" tag="p" bold={false}>
                  {warehouseData?.line1}
                </Text>
                <Text level="Body2" tag="p" bold={false}>
                  {warehouseData?.line2}
                </Text>
                <Text level="Body2" tag="p" bold={false}>
                  {warehouseData?.locality + ', ' + warehouseData?.region + ' ' + warehouseData?.postcode}
                </Text>
              </div>
              <div id="additional-wh-info">
                <div>{HeaderDetail({label: 'Receiving Hours:', value: receivingHrs})}</div>
                <div>{HeaderDetail({label: 'Primary Contact:', value: userName})}</div>
                <div>{HeaderDetail({label: 'Phone Number:', value: phone ? phone : 'Not Specified'})}</div>
              </div>
            </Row>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WarehouseInformation;
